import * as styles from "./ContactRelations.css";

import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";

import { Icon } from "@frontend/components/atoms/Icon";
import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import { Spinner } from "@frontend/components/atoms/Spinner";
import { Trans } from "@lingui/react/macro";

import { useRelationSections } from "../../hooks/useRelationSections";
import {
  loadRelationsAtom,
  relationsAtom,
  relationsFailedAtom,
  relationsLoadingAtom,
  selectedContactAtom,
} from "../../stores";
import { RelationSection } from "./RelationSection";

/**
 * What the contact is tied to across the application. Fetched apart from the
 * contact itself, and only once its tab is opened.
 */
export function ContactRelations() {
  const selectedContact = useAtomValue(selectedContactAtom);
  const links = useAtomValue(relationsAtom);
  const loading = useAtomValue(relationsLoadingAtom);
  const failed = useAtomValue(relationsFailedAtom);

  const loadRelations = useSetAtom(loadRelationsAtom);

  const contactId = selectedContact?.id;

  useEffect(() => {
    if (contactId) {
      void loadRelations(contactId);
    }
  }, [contactId, loadRelations]);

  const sections = useRelationSections(links);

  if (loading) {
    return (
      <div className={styles.message}>
        <Spinner />
      </div>
    );
  }

  if (failed) {
    return (
      <div className={styles.message}>
        <Icon name="TriangleAlert" size="lg" />
        <Trans>Could not load the linked records</Trans>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className={styles.message}>
        <Icon name="Unlink" size="lg" />
        <Trans>This contact is not linked to anything</Trans>
      </div>
    );
  }

  return (
    <ScrollArea className={styles.scrollArea}>
      <ScrollAreaViewport fade>
        <ScrollAreaContent className={styles.content}>
          {sections.map((section) => (
            <RelationSection
              key={section.entity}
              entity={section.entity}
              links={section.links}
            />
          ))}
        </ScrollAreaContent>
      </ScrollAreaViewport>

      <ScrollAreaScrollbar alwaysVisible expandOnHover>
        <ScrollAreaThumb />
      </ScrollAreaScrollbar>
    </ScrollArea>
  );
}
